import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Popover } from "antd";
import type { MenuProps } from "antd";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AuthService } from "../../services";
import TokenManager from "../../utils/tokenManager";
import ErrorHandler from "../../utils/errorHandler";
import { Avatar, Icon } from "../ui";
import { ADMIN_CONTACT_NOTE, findNav, ROLE_LABEL, visibleGroups } from "../../config/navigation";
import "../../styles/MainLayout.scss";

interface MainLayoutProps {
  children: React.ReactNode;
  /** 覆盖导航里的页面标题 */
  title?: string;
  /** 覆盖导航里的页面说明 */
  description?: string;
}

const COLLAPSE_KEY = "ddp:sidebar-collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

const MainLayout: React.FC<MainLayoutProps> = ({ children, title, description }) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const media = window.matchMedia("(max-width: 820px)");
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, prev ? "0" : "1");
      } catch {
        /* 浏览器禁用存储时忽略 */
      }
      return !prev;
    });
  };

  const payload = TokenManager.getUserFromToken(TokenManager.getAccessToken() || "") || {};
  const roles: string[] = Array.isArray(payload.roles) ? payload.roles : [];
  const primaryRole = roles.includes("admin") ? "admin" : roles.includes("org_manager") ? "org_manager" : roles[0] || "customer";
  const displayName: string = payload.username || payload.nickname || payload.phone || "当前账号";
  const groups = useMemo(() => visibleGroups(roles), [roles.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = findNav(location.pathname);
  const pageTitle = title || current?.item.label || "运营工作台";
  const pageDescription = description || current?.item.description || "让每一次运营动作都可追踪";
  const narrow = collapsed && !isMobile;

  const handleLogout = async () => {
    try {
      await AuthService.logout();
    } catch (error) {
      ErrorHandler.handleApiError(error, false);
    } finally {
      TokenManager.clearToken();
      navigate("/login");
    }
  };

  const userMenu: MenuProps["items"] = [
    {
      key: "who",
      disabled: true,
      label: (
        <div className="ddp-user-menu__who">
          <strong>{displayName}</strong>
          <span>{ROLE_LABEL[primaryRole] || primaryRole}{payload.phone ? ` · ${payload.phone}` : ""}</span>
        </div>
      ),
    },
    { type: "divider" },
    { key: "home", icon: <Icon name="home" />, label: "返回官网首页", onClick: () => navigate("/") },
    { key: "agreement", icon: <Icon name="file" />, label: "用户协议与隐私说明", onClick: () => navigate("/user-privacy-agreement") },
    { type: "divider" },
    { key: "logout", icon: <Icon name="logout" />, label: "退出登录", danger: true, onClick: handleLogout },
  ];

  const supportCard = (
    <div className="ddp-support">
      <strong>需要帮助？</strong>
      <p>{ADMIN_CONTACT_NOTE}</p>
      <NavLink to="/user-privacy-agreement">用户协议与隐私说明</NavLink>
    </div>
  );

  return (
    <div className={`ddp-shell${narrow ? " is-collapsed" : ""}${mobileOpen ? " is-mobile-open" : ""}`}>
      {isMobile && mobileOpen && <button type="button" className="ddp-scrim" aria-label="关闭导航" onClick={() => setMobileOpen(false)} />}
      <aside className="ddp-sidebar" aria-label="侧边栏">
        <div className="ddp-sidebar__brand">
          <img src="/brand/duoduopin-mark-256.png" alt="多多拼" width={34} height={34} />
          {!narrow && (
            <span>
              <strong>多多拼</strong>
              <small>运营工作台</small>
            </span>
          )}
        </div>
        <nav className="ddp-nav" aria-label="主导航">
          {groups.map((group) => (
            <div className="ddp-nav__group" key={group.label}>
              {!narrow && <div className="ddp-nav__label">{group.label}</div>}
              {group.items.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.path}
                  className={({ isActive }) => `ddp-nav__item${isActive ? " is-active" : ""}`}
                  title={narrow ? item.label : undefined}
                >
                  <span className="ddp-nav__fillet ddp-nav__fillet--top" aria-hidden="true" />
                  <span className="ddp-nav__fillet ddp-nav__fillet--bottom" aria-hidden="true" />
                  <Icon name={item.icon} />
                  {!narrow && <span className="ddp-nav__text">{item.label}</span>}
                  {narrow && <span className="visually-hidden">{item.label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="ddp-sidebar__foot">
          <Popover content={supportCard} placement="rightBottom" trigger={["click", "hover"]}>
            <button type="button" className="ddp-sidebar__help" aria-label="帮助">
              <Icon name="info" />
              {!narrow && <span>帮助</span>}
            </button>
          </Popover>
          {!isMobile && (
            <button type="button" className="ddp-sidebar__toggle" aria-label={narrow ? "展开导航" : "收起导航"} onClick={toggleCollapsed}>
              <Icon name="sidebar" />
            </button>
          )}
        </div>
      </aside>

      <div className="ddp-stage">
        <header className="ddp-header">
          {isMobile && (
            <button type="button" className="ddp-header__menu" aria-label="打开导航" onClick={() => setMobileOpen(true)}>
              <Icon name="sidebar" size={18} />
            </button>
          )}
          <div className="ddp-header__title">
            <h1>{pageTitle}</h1>
            <p>{pageDescription}</p>
          </div>
          <div className="ddp-header__right">
            {current && (
              <nav className="ddp-crumb" aria-label="面包屑">
                <span>{current.group.label}</span>
                <span aria-hidden="true" className="ddp-crumb__sep">/</span>
                <span className="ddp-crumb__current">{current.item.label}</span>
              </nav>
            )}
            {current && <span className="ddp-header__rule" aria-hidden="true" />}
            <Dropdown menu={{ items: userMenu }} trigger={["click"]} placement="bottomRight" overlayClassName="ddp-user-menu">
              <button type="button" className="ddp-user" aria-label="账号菜单" aria-haspopup="menu">
                <Avatar text={displayName} tone="ink" size={32} />
                <span className="ddp-user__text">
                  <strong>{displayName}</strong>
                  <small>{ROLE_LABEL[primaryRole] || primaryRole}</small>
                </span>
                <Icon name="chevD" size={15} />
              </button>
            </Dropdown>
          </div>
        </header>
        <main className="ddp-main">
          <div className="ddp-main__frame">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
